import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../../services/supabaseClient';
import { 
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, 
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import DatePicker from 'react-datepicker';
import { registerLocale } from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import frLocale from 'date-fns/locale/fr';

// Enregistrer la localisation française pour le DatePicker
registerLocale('fr', frLocale);

const Analytics = () => {
  const [loading, setLoading] = useState(false);
  const [chatLoading, setChatLoading] = useState(false);
  const [stats, setStats] = useState({
    totalVisits: 0,
    uniqueVisitors: 0,
    pageViews: {},
    browsers: {},
    devices: {},
    topReferrers: [],
    dailyVisits: [],
    loadingProgress: null
  });
  const [chatStats, setChatStats] = useState({
    totalMessages: 0,
    uniqueUsers: 0,
    topIntents: [],
    topTopics: [],
    messagesByLanguage: {},
    dailyMessages: [],
    recentMessages: [],
    loadingProgress: null
  });
  const [dateRange, setDateRange] = useState('week'); // 'today', 'week', 'month', 'all', 'custom'
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [activeTab, setActiveTab] = useState('visits'); // 'visits' ou 'chatbot'
  const datePickerRef = useRef(null);
  
  // Couleurs de la charte graphique
  const COLORS = {
    primary: '#e88c00', // orange
    secondary: '#0b8e36', // vert
    tertiary: '#f8b456', // orange clair
    quaternary: '#4ebb6e', // vert clair
    neutral: '#718096', // gris
    neutralLight: '#E2E8F0', // gris clair
    chart: ['#e88c00', '#0b8e36', '#f8b456', '#4ebb6e', '#718096', '#2D3748', '#A0AEC0', '#F56565', '#ED8936', '#ECC94B']
  };
  
  // Fonction pour afficher la barre de progression
  const renderProgressBar = (progress) => {
    if (!progress) return null;
    
    const { current, total, message } = progress;
    const percentage = Math.min(Math.round((current / total) * 100), 100);
    
    return (
      <div className="mb-4">
        <div className="flex justify-between items-center mb-1">
          <span className="text-sm font-medium text-gray-700">{message}</span>
          <span className="text-sm font-medium text-gray-700">{percentage}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2.5">
          <div 
            className="bg-orange-500 h-2.5 rounded-full" 
            style={{ width: `${percentage}%` }}
          ></div>
        </div>
      </div>
    );
  };

  // Fonction pour gérer le changement de période
  const handleDateRangeChange = (e) => {
    const newRange = e.target.value;
    setDateRange(newRange);
    
    // Initialiser les dates pour la période personnalisée
    if (newRange === 'custom') {
      setShowDatePicker(true);
      // Par défaut, définir la période personnalisée sur les 7 derniers jours
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 7);
      setStartDate(start);
      setEndDate(end);
    } else {
      setShowDatePicker(false);
    }
  };

  // Fonction pour récupérer les statistiques de visites
  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      // Préparer la requête de base
      let baseQuery = supabase.from('site_visits');

      // Préparer les filtres de date
      const now = new Date();
      let queryStartDate;
      let queryEndDate = new Date(now);
      
      if (dateRange === 'today') {
        queryStartDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      } else if (dateRange === 'week') {
        queryStartDate = new Date(now);
        queryStartDate.setDate(queryStartDate.getDate() - 7);
      } else if (dateRange === 'month') {
        queryStartDate = new Date(now);
        queryStartDate.setMonth(queryStartDate.getMonth() - 1);
      } else if (dateRange === 'custom') {
        queryStartDate = new Date(startDate);
        queryEndDate = new Date(endDate);
        // Ajouter un jour à la date de fin pour inclure tout le jour sélectionné
        queryEndDate.setDate(queryEndDate.getDate() + 1);
      } else {
        queryStartDate = new Date(2023, 0, 1); // Début de l'année 2023 comme date par défaut pour 'all'
      }

      // Mettre à jour l'état pour afficher le début du chargement
      setStats(prev => ({
        ...prev,
        loadingProgress: {
          current: 0,
          total: 100,
          message: 'Comptage des enregistrements...'
        }
      }));

      // Étape 1: Compter le nombre total d'enregistrements
      let countQuery = baseQuery.select('id', { count: 'exact', head: true });
      
      // Appliquer les filtres de date
      if (dateRange === 'custom') {
        countQuery = countQuery
          .gte('visited_at', queryStartDate.toISOString())
          .lt('visited_at', queryEndDate.toISOString());
      } else {
        countQuery = countQuery.gte('visited_at', queryStartDate.toISOString());
      }
      
      const { count, error: countError } = await countQuery;
      
      if (countError) {
        console.error('Erreur lors du comptage des visites:', countError);
        setStats(prev => ({ ...prev, loadingProgress: null }));
        return;
      }

      if (!count || count === 0) {
        // Pas de données à traiter
        setStats(prev => ({
          ...prev,
          totalVisits: 0,
          uniqueVisitors: 0,
          pageViews: {},
          browsers: {},
          devices: {},
          topReferrers: [],
          dailyVisits: [],
          loadingProgress: null
        }));
        return;
      }

      // Initialiser les structures de données pour stocker les résultats
      const pageViews = {};
      const browsers = {};
      const devices = {};
      const referrers = {};
      const uniqueSessions = new Set();
      const dailyVisitsMap = {};
      const dailyUniqueSessions = {};
      
      // Initialiser les données quotidiennes
      let currentDate = new Date(queryStartDate);
      while (currentDate <= queryEndDate) {
        const dateKey = format(currentDate, 'yyyy-MM-dd', { locale: fr });
        dailyVisitsMap[dateKey] = { date: dateKey, visits: 0, uniqueVisitors: 0 };
        currentDate.setDate(currentDate.getDate() + 1);
      }

      // Étape 2: Récupérer les données par lots
      const batchSize = 1000;
      const totalBatches = Math.ceil(count / batchSize);
      
      for (let batch = 0; batch < totalBatches; batch++) {
        const from = batch * batchSize;
        const to = Math.min((batch + 1) * batchSize - 1, count - 1);
        
        // Mettre à jour l'état de chargement
        setStats(prev => ({
          ...prev,
          loadingProgress: {
            current: from,
            total: count,
            message: `Récupération du lot ${batch + 1}/${totalBatches}...`
          }
        }));
        
        // Préparer la requête pour ce lot
        let batchQuery = baseQuery.select('*').range(from, to);
        
        // Appliquer les filtres de date
        if (dateRange === 'custom') {
          batchQuery = batchQuery
            .gte('visited_at', queryStartDate.toISOString())
            .lt('visited_at', queryEndDate.toISOString());
        } else {
          batchQuery = batchQuery.gte('visited_at', queryStartDate.toISOString());
        }
        
        const { data: batchData, error: batchError } = await batchQuery;
        
        if (batchError) {
          console.error(`Erreur lors de la récupération du lot ${batch + 1}:`, batchError);
          continue;
        }
        
        if (!batchData || batchData.length === 0) continue;
        
        // Traiter les données du lot
        batchData.forEach(visit => {
          // Compter les pages vues
          const page = visit.page_url || '/';
          pageViews[page] = (pageViews[page] || 0) + 1;

          // Compter les navigateurs
          const browser = visit.browser || 'Inconnu';
          browsers[browser] = (browsers[browser] || 0) + 1;

          // Compter les appareils
          const device = visit.device_type || 'Inconnu';
          devices[device] = (devices[device] || 0) + 1;

          // Compter les référents
          if (visit.referrer) {
            referrers[visit.referrer] = (referrers[visit.referrer] || 0) + 1;
          }

          // Compter les sessions uniques
          if (visit.session_id) {
            uniqueSessions.add(visit.session_id);
          }
          
          // Traiter les données quotidiennes
          if (visit.visited_at) {
            const visitDate = new Date(visit.visited_at);
            const dateKey = format(visitDate, 'yyyy-MM-dd', { locale: fr });
            
            if (dailyVisitsMap[dateKey]) {
              dailyVisitsMap[dateKey].visits += 1;
              
              // Initialiser l'ensemble des sessions uniques pour ce jour si nécessaire
              if (!dailyUniqueSessions[dateKey]) {
                dailyUniqueSessions[dateKey] = new Set();
              }
              
              // Ajouter la session à l'ensemble des sessions uniques pour ce jour
              if (visit.session_id) {
                dailyUniqueSessions[dateKey].add(visit.session_id);
              }
            }
          }
        });
        
        // Mettre à jour l'état de chargement après traitement du lot
        setStats(prev => ({
          ...prev,
          loadingProgress: {
            current: to + 1,
            total: count,
            message: `Traitement des données (${Math.min(to + 1, count)}/${count})...`
          }
        }));
      }
      
      // Mettre à jour le nombre de visiteurs uniques par jour
      Object.keys(dailyUniqueSessions).forEach(dateKey => {
        if (dailyVisitsMap[dateKey]) {
          dailyVisitsMap[dateKey].uniqueVisitors = dailyUniqueSessions[dateKey].size;
        }
      });

      // Convertir la map en tableau pour les graphiques
      const dailyVisits = Object.values(dailyVisitsMap).sort((a, b) => 
        new Date(a.date) - new Date(b.date)
      );

      // Trier les référents par nombre de visites
      const topReferrers = Object.entries(referrers)
        .map(([url, count]) => ({ url, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);

      // Calculer le nombre total de visites
      const totalVisits = Object.values(pageViews).reduce((sum, count) => sum + count, 0);

      // Mettre à jour les statistiques finales
      setStats({
        totalVisits,
        uniqueVisitors: uniqueSessions.size,
        pageViews,
        browsers,
        devices,
        topReferrers,
        dailyVisits,
        loadingProgress: null
      });
    } catch (error) {
      console.error('Erreur inattendue:', error);
      setStats(prev => ({ ...prev, loadingProgress: null }));
    } finally {
      setLoading(false);
    }
  }, [dateRange, startDate, endDate]);

  // Fonction pour récupérer les statistiques du chatbot
  const fetchChatAnalytics = useCallback(async () => {
    setChatLoading(true);
    try {
      // Préparer la requête de base
      let baseQuery = supabase.from('chat_messages');

      // Préparer les filtres de date
      const now = new Date();
      let queryStartDate;
      let queryEndDate = new Date(now);
      
      if (dateRange === 'today') {
        queryStartDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      } else if (dateRange === 'week') {
        queryStartDate = new Date(now);
        queryStartDate.setDate(queryStartDate.getDate() - 7);
      } else if (dateRange === 'month') {
        queryStartDate = new Date(now);
        queryStartDate.setMonth(queryStartDate.getMonth() - 1);
      } else if (dateRange === 'custom') {
        queryStartDate = new Date(startDate);
        queryEndDate = new Date(endDate);
        // Ajouter un jour à la date de fin pour inclure tout le jour sélectionné
        queryEndDate.setDate(queryEndDate.getDate() + 1);
      } else {
        queryStartDate = new Date(2023, 0, 1); // Début de l'année 2023 comme date par défaut pour 'all'
      }

      // Mettre à jour l'état pour afficher le début du chargement
      setChatStats(prev => ({
        ...prev,
        loadingProgress: {
          current: 0,
          total: 100,
          message: 'Comptage des messages du chatbot...'
        }
      }));

      // Étape 1: Compter le nombre total d'enregistrements
      let countQuery = baseQuery.select('id', { count: 'exact', head: true });
      
      // Appliquer les filtres de date
      if (dateRange === 'custom') {
        countQuery = countQuery
          .gte('created_at', queryStartDate.toISOString())
          .lt('created_at', queryEndDate.toISOString());
      } else {
        countQuery = countQuery.gte('created_at', queryStartDate.toISOString());
      }
      
      const { count, error: countError } = await countQuery;
      
      if (countError) {
        console.error('Erreur lors du comptage des messages du chatbot:', countError);
        setChatStats(prev => ({ ...prev, loadingProgress: null }));
        return;
      }

      if (!count || count === 0) {
        // Pas de données à traiter
        setChatStats(prev => ({
          ...prev,
          totalMessages: 0,
          uniqueUsers: 0,
          topIntents: [],
          topTopics: [],
          messagesByLanguage: {},
          dailyMessages: [],
          recentMessages: [],
          loadingProgress: null
        }));
        return;
      }

      // Initialiser les structures de données pour stocker les résultats
      const intents = {};
      const topics = {};
      const languages = {};
      const uniqueIPs = new Set();
      const dailyMessagesMap = {};
      let allRecentMessages = [];
      
      // Initialiser les données quotidiennes
      let currentDate = new Date(queryStartDate);
      while (currentDate <= queryEndDate) {
        const dateKey = format(currentDate, 'yyyy-MM-dd', { locale: fr });
        dailyMessagesMap[dateKey] = { date: dateKey, messages: 0 };
        currentDate.setDate(currentDate.getDate() + 1);
      }

      // Étape 2: Récupérer les données par lots
      const batchSize = 1000;
      const totalBatches = Math.ceil(count / batchSize);
      
      for (let batch = 0; batch < totalBatches; batch++) {
        const from = batch * batchSize;
        const to = Math.min((batch + 1) * batchSize - 1, count - 1);
        
        // Mettre à jour l'état de chargement
        setChatStats(prev => ({
          ...prev,
          loadingProgress: {
            current: from,
            total: count,
            message: `Récupération du lot ${batch + 1}/${totalBatches}...`
          }
        }));
        
        // Préparer la requête pour ce lot
        let batchQuery = baseQuery.select('*').range(from, to);
        
        // Appliquer les filtres de date
        if (dateRange === 'custom') {
          batchQuery = batchQuery
            .gte('created_at', queryStartDate.toISOString())
            .lt('created_at', queryEndDate.toISOString());
        } else {
          batchQuery = batchQuery.gte('created_at', queryStartDate.toISOString());
        }
        
        const { data: batchData, error: batchError } = await batchQuery;
        
        if (batchError) {
          console.error(`Erreur lors de la récupération du lot ${batch + 1}:`, batchError);
          continue;
        }
        
        if (!batchData || batchData.length === 0) continue;
        
        // Traiter les données du lot
        batchData.forEach(message => {
          // Compter les intents
          if (message.intent) {
            intents[message.intent] = (intents[message.intent] || 0) + 1;
          }

          // Compter les topics
          if (message.chat_topic) {
            topics[message.chat_topic] = (topics[message.chat_topic] || 0) + 1;
          }

          // Compter les langues
          const language = message.language || 'Inconnu';
          languages[language] = (languages[language] || 0) + 1;

          // Compter les utilisateurs uniques par IP
          if (message.ip_address) {
            uniqueIPs.add(message.ip_address);
          }
          
          // Traiter les données quotidiennes
          if (message.created_at) {
            const messageDate = new Date(message.created_at);
            const dateKey = format(messageDate, 'yyyy-MM-dd', { locale: fr });
            
            if (dailyMessagesMap[dateKey]) {
              dailyMessagesMap[dateKey].messages += 1;
            }
          }
        });
        
        // Collecter les messages récents pour ce lot
        if (batch === 0) { // Ne prendre les messages récents que du premier lot
          const batchRecentMessages = [...batchData]
            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
            .slice(0, 10);
          allRecentMessages = batchRecentMessages;
        }
        
        // Mettre à jour l'état de chargement après traitement du lot
        setChatStats(prev => ({
          ...prev,
          loadingProgress: {
            current: to + 1,
            total: count,
            message: `Traitement des données (${Math.min(to + 1, count)}/${count})...`
          }
        }));
      }

      // Convertir la map en tableau pour les graphiques
      const dailyMessages = Object.values(dailyMessagesMap).sort((a, b) => 
        new Date(a.date) - new Date(b.date)
      );

      // Trier les intents par nombre d'occurrences
      const topIntents = Object.entries(intents)
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);

      // Trier les topics par nombre d'occurrences
      const topTopics = Object.entries(topics)
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);
        
      // Mettre à jour les statistiques finales
      setChatStats({
        totalMessages: count,
        uniqueUsers: uniqueIPs.size,
        topIntents,
        topTopics,
        messagesByLanguage: languages,
        dailyMessages,
        recentMessages: allRecentMessages,
        loadingProgress: null
      });
    } catch (error) {
      console.error('Erreur inattendue:', error);
      setChatStats(prev => ({ ...prev, loadingProgress: null }));
    } finally {
      setChatLoading(false);
    }
  }, [dateRange, startDate, endDate]);
  
  // Utilisation des fonctions dans useEffect
  useEffect(() => {
    fetchAnalytics();
    fetchChatAnalytics();
  }, [fetchAnalytics, fetchChatAnalytics]);
  
  // La fonction handleDateRangeChange est déjà définie plus haut dans le code
  // La fonction fetchAnalytics est déjà définie plus haut dans le code avec useCallback
  
  /*
    setLoading(true);
    try {
      // Construire la requête de base
      let query = supabase.from('site_visits').select('*');

      // Filtrer par date
      const now = new Date();
      let queryStartDate;
      let queryEndDate = new Date(now);
      
      if (dateRange === 'today') {
        queryStartDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        query = query.gte('visited_at', queryStartDate.toISOString());
      } else if (dateRange === 'week') {
        queryStartDate = new Date(now);
        queryStartDate.setDate(queryStartDate.getDate() - 7);
        query = query.gte('visited_at', queryStartDate.toISOString());
      } else if (dateRange === 'month') {
        queryStartDate = new Date(now);
        queryStartDate.setMonth(queryStartDate.getMonth() - 1);
        query = query.gte('visited_at', queryStartDate.toISOString());
      } else if (dateRange === 'custom') {
        queryStartDate = new Date(startDate);
        queryEndDate = new Date(endDate);
        // Ajouter un jour à la date de fin pour inclure tout le jour sélectionné
        queryEndDate.setDate(queryEndDate.getDate() + 1);
        query = query
          .gte('visited_at', queryStartDate.toISOString())
          .lt('visited_at', queryEndDate.toISOString());
      } else {
        queryStartDate = new Date(2023, 0, 1); // Début de l'année 2023 comme date par défaut pour 'all'
      }

      // Exécuter la requête
      const { data, error } = await query;

      if (error) {
        console.error('Erreur lors de la récupération des statistiques:', error);
        return;
      }

      // Traiter les données
      const pageViews = {};
      const browsers = {};
      const devices = {};
      const referrers = {};
      const uniqueSessions = new Set();
      const dailyVisitsMap = {};
      
      // Initialiser les données quotidiennes
      let currentDate = new Date(queryStartDate);
      
      while (currentDate <= queryEndDate) {
        const dateKey = format(currentDate, 'yyyy-MM-dd', { locale: fr });
        dailyVisitsMap[dateKey] = { date: dateKey, visits: 0, uniqueVisitors: 0 };
        currentDate.setDate(currentDate.getDate() + 1);
      }

      // Ensemble pour suivre les visiteurs uniques par jour
      const dailyUniqueSessions = {};

      data.forEach(visit => {
        // Compter les pages vues
        const page = visit.page_url || '/';
        pageViews[page] = (pageViews[page] || 0) + 1;

        // Compter les navigateurs
        const browser = visit.browser || 'Inconnu';
        browsers[browser] = (browsers[browser] || 0) + 1;

        // Compter les appareils
        const device = visit.device_type || 'Inconnu';
        devices[device] = (devices[device] || 0) + 1;

        // Compter les référents
        if (visit.referrer) {
          referrers[visit.referrer] = (referrers[visit.referrer] || 0) + 1;
        }

        // Compter les sessions uniques
        if (visit.session_id) {
          uniqueSessions.add(visit.session_id);
        }
        
        // Traiter les données quotidiennes
        if (visit.visited_at) {
          const visitDate = new Date(visit.visited_at);
          const dateKey = format(visitDate, 'yyyy-MM-dd', { locale: fr });
          
          if (dailyVisitsMap[dateKey]) {
            dailyVisitsMap[dateKey].visits += 1;
            
            // Initialiser l'ensemble des sessions uniques pour ce jour si nécessaire
            if (!dailyUniqueSessions[dateKey]) {
              dailyUniqueSessions[dateKey] = new Set();
            }
            
            // Ajouter la session à l'ensemble des sessions uniques pour ce jour
            if (visit.session_id) {
              dailyUniqueSessions[dateKey].add(visit.session_id);
            }
          }
        }
      });
      
      // Mettre à jour le nombre de visiteurs uniques par jour
      Object.keys(dailyUniqueSessions).forEach(dateKey => {
        if (dailyVisitsMap[dateKey]) {
          dailyVisitsMap[dateKey].uniqueVisitors = dailyUniqueSessions[dateKey].size;
        }
      });

      // Convertir la map en tableau pour les graphiques
      const dailyVisits = Object.values(dailyVisitsMap).sort((a, b) => 
        new Date(a.date) - new Date(b.date)
      );

      // Trier les référents par nombre de visites
      const topReferrers = Object.entries(referrers)
        .map(([url, count]) => ({ url, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);

      // Mettre à jour les statistiques
      setStats({
        totalVisits: data.length,
        uniqueVisitors: uniqueSessions.size,
        pageViews,
        browsers,
        devices,
        topReferrers,
        dailyVisits
      });
    } catch (error) {
      console.error('Erreur inattendue:', error);
    } finally {
      setLoading(false);
    }
  }; */

  // Fonction pour obtenir les entrées triées d'un objet
  const getSortedEntries = (obj) => {
    return Object.entries(obj)
      .map(([key, value]) => ({ key, value }))
      .sort((a, b) => b.value - a.value);
  };

  // Composant pour afficher un graphique en courbes des visites quotidiennes
  const VisitsTrendChart = () => {
    if (!stats.dailyVisits || stats.dailyVisits.length === 0) {
      return <div className="p-6 text-center">Aucune donnée disponible</div>;
    }

    return (
      <ResponsiveContainer width="100%" height={300}>
        <LineChart
          data={stats.dailyVisits}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke={COLORS.neutralLight} />
          <XAxis 
            dataKey="date" 
            tick={{ fill: COLORS.neutral }} 
            tickFormatter={(value) => {
              const date = new Date(value);
              return format(date, 'dd/MM', { locale: fr });
            }}
          />
          <YAxis tick={{ fill: COLORS.neutral }} />
          <Tooltip 
            formatter={(value) => [value, 'Visites']}
            labelFormatter={(value) => format(new Date(value), 'dd MMMM yyyy', { locale: fr })}
          />
          <Legend />
          <Line 
            type="monotone" 
            dataKey="visits" 
            name="Visites" 
            stroke={COLORS.primary} 
            activeDot={{ r: 8, fill: COLORS.primary }}
            strokeWidth={2}
          />
          <Line 
            type="monotone" 
            dataKey="uniqueVisitors" 
            name="Visiteurs uniques" 
            stroke={COLORS.secondary} 
            activeDot={{ r: 8, fill: COLORS.secondary }}
            strokeWidth={2}
          />
        </LineChart>
      </ResponsiveContainer>
    );
  };

  // Composant pour afficher un graphique en barres des pages les plus visitées
  const TopPagesChart = () => {
    const data = getSortedEntries(stats.pageViews)
      .slice(0, 5)
      .map(({ key, value }) => ({
        name: key === '/' ? 'Accueil' : key.length > 20 ? key.substring(0, 20) + '...' : key,
        value,
        fullName: key
      }));

    return (
      <ResponsiveContainer width="100%" height={200}>
        <BarChart
          data={data}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          layout="vertical"
        >
          <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke={COLORS.neutralLight} />
          <XAxis type="number" tick={{ fill: COLORS.neutral }} />
          <YAxis 
            type="category" 
            dataKey="name" 
            tick={{ fill: COLORS.neutral }} 
            width={120}
          />
          <Tooltip 
            formatter={(value) => [value, 'Visites']}
            labelFormatter={(_, payload) => payload && payload[0] ? payload[0].fullName : ''}
          />
          <Bar 
            dataKey="value" 
            fill={COLORS.primary} 
            radius={[0, 4, 4, 0]}
            barSize={20}
          />
        </BarChart>
      </ResponsiveContainer>
    );
  };

  // Composant pour afficher un graphique en camembert des navigateurs
  const BrowsersPieChart = () => {
    const data = getSortedEntries(stats.browsers).map(({ key, value }) => ({
      name: key,
      value
    }));

    // Fonction de rendu personnalisée pour les libellés
    const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index, name }) => {
      // Ne pas afficher de libellé sur le graphique lui-même
      // Les informations seront visibles dans la légende et les tooltips
      return null;
    };

    return (
      <div>
        <ResponsiveContainer width="100%" height={200}>
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              labelLine={false}
              outerRadius={80}
              fill="#8884d8"
              dataKey="value"
              nameKey="name"
              label={renderCustomizedLabel}
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS.chart[index % COLORS.chart.length]} />
              ))}
            </Pie>
            <Tooltip formatter={(value, name) => [`${value} visites (${((value / stats.totalVisits) * 100).toFixed(0)}%)`, name]} />
            <Legend formatter={(value, entry, index) => `${value}`} />
          </PieChart>
        </ResponsiveContainer>
      </div>
    );
  };

  // Composant pour afficher un graphique en camembert des appareils
  const DevicesPieChart = () => {
    const data = getSortedEntries(stats.devices).map(({ key, value }) => ({
      name: key,
      value
    }));

    // Fonction de rendu personnalisée pour les libellés
    const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index, name }) => {
      // Ne pas afficher de libellé sur le graphique lui-même
      // Les informations seront visibles dans la légende et les tooltips
      return null;
    };

    return (
      <div>
        <ResponsiveContainer width="100%" height={200}>
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              labelLine={false}
              outerRadius={80}
              fill="#8884d8"
              dataKey="value"
              nameKey="name"
              label={renderCustomizedLabel}
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS.chart[index % COLORS.chart.length]} />
              ))}
            </Pie>
            <Tooltip formatter={(value, name) => [`${value} visites (${((value / stats.totalVisits) * 100).toFixed(0)}%)`, name]} />
            <Legend formatter={(value, entry, index) => `${value}`} />
          </PieChart>
        </ResponsiveContainer>
      </div>
    );
  };

  // Composant pour afficher un graphique en courbes des messages du chatbot
  // eslint-disable-next-line no-unused-vars
  const ChatMessagesChart = () => {
    if (!chatStats.dailyMessages || chatStats.dailyMessages.length === 0) {
      return <div className="p-6 text-center">Aucune donnée disponible</div>;
    }

    return (
      <ResponsiveContainer width="100%" height={300}>
        <LineChart
          data={chatStats.dailyMessages}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke={COLORS.neutralLight} />
          <XAxis 
            dataKey="date" 
            tick={{ fill: COLORS.neutral }} 
            tickFormatter={(value) => {
              const date = new Date(value);
              return format(date, 'dd/MM', { locale: fr });
            }}
          />
          <YAxis tick={{ fill: COLORS.neutral }} />
          <Tooltip 
            formatter={(value) => [value, 'Messages']}
            labelFormatter={(value) => format(new Date(value), 'dd MMMM yyyy', { locale: fr })}
          />
          <Legend />
          <Line 
            type="monotone" 
            dataKey="messages" 
            name="Messages" 
            stroke={COLORS.secondary} 
            activeDot={{ r: 8, fill: COLORS.secondary }}
            strokeWidth={2}
          />
        </LineChart>
      </ResponsiveContainer>
    );
  };
  
  // Composant pour afficher un graphique en camembert des langues utilisées dans le chatbot
  // eslint-disable-next-line no-unused-vars
  const ChatLanguagesPieChart = () => {
    const data = getSortedEntries(chatStats.messagesByLanguage).map(({ key, value }) => ({
      name: key,
      value
    }));

    return (
      <ResponsiveContainer width="100%" height={200}>
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            labelLine={false}
            outerRadius={80}
            fill="#8884d8"
            dataKey="value"
            nameKey="name"
            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
          >
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={COLORS.chart[index % COLORS.chart.length]} />
            ))}
          </Pie>
          <Tooltip formatter={(value) => [`${value} messages`, null]} />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    );
  };
  
  // Composant pour afficher un graphique en barres des intents du chatbot
  const ChatIntentsChart = () => {
    const data = chatStats.topIntents.map(item => ({
      name: item.name.length > 15 ? item.name.substring(0, 15) + '...' : item.name,
      value: item.count,
      fullName: item.name,
      percentage: ((item.count / chatStats.totalMessages) * 100).toFixed(1)
    }));

    return (
      <ResponsiveContainer width="100%" height={250}>
        <BarChart
          data={data}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          layout="vertical"
        >
          <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke={COLORS.neutralLight} />
          <XAxis type="number" tick={{ fill: COLORS.neutral }} />
          <YAxis 
            type="category" 
            dataKey="name" 
            tick={{ fill: COLORS.neutral }} 
            width={120}
          />
          <Tooltip 
            // eslint-disable-next-line react/prop-types
            formatter={(value, name, props) => [`${value} (${props.payload.percentage}%)`, 'Messages']}
            // eslint-disable-next-line react/prop-types
            labelFormatter={(_, payload) => payload && payload[0] ? payload[0].fullName : ''}
            contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', borderRadius: '8px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}
          />
          <Bar 
            dataKey="value" 
            fill={COLORS.secondary} 
            radius={[0, 4, 4, 0]}
            barSize={20}
            label={{
              position: 'right',
              // eslint-disable-next-line react/prop-types
              content: (props) => {
                // eslint-disable-next-line react/prop-types
                const { x, y, width, height, value } = props;
                return (
                  <g>
                    <text x={x + width + 5} y={y + height / 2} fill={COLORS.neutral} textAnchor="start" dominantBaseline="middle">
                      {value}
                    </text>
                  </g>
                );
              }
            }}
          />
        </BarChart>
      </ResponsiveContainer>
    );
  };

  return (
    <div className="p-6 bg-gray-50">
      <h1 className="text-3xl font-bold mb-6 text-gray-800">Statistiques</h1>
      
      {/* Onglets de navigation */}
      <div className="flex border-b border-gray-200 mb-6">
        <button
          className={`py-2 px-4 font-medium text-sm focus:outline-none ${activeTab === 'visits' ? 'border-b-2 border-primary-500 text-primary-600' : 'text-gray-500 hover:text-gray-700'}`}
          onClick={() => setActiveTab('visits')}
          style={{ borderColor: activeTab === 'visits' ? COLORS.primary : '', color: activeTab === 'visits' ? COLORS.primary : '' }}
        >
          Statistiques de visites
        </button>
        <button
          className={`py-2 px-4 font-medium text-sm focus:outline-none ${activeTab === 'chatbot' ? 'border-b-2 border-primary-500 text-primary-600' : 'text-gray-500 hover:text-gray-700'}`}
          onClick={() => setActiveTab('chatbot')}
          style={{ borderColor: activeTab === 'chatbot' ? COLORS.secondary : '', color: activeTab === 'chatbot' ? COLORS.secondary : '' }}
        >
          Statistiques du Chatbot
        </button>
      </div>
      
      {/* Sélecteur de période */}
      <div className="mb-6 bg-white p-4 rounded-lg shadow-sm flex gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Période d&apos;analyse</label>
          <select
            value={dateRange}
            onChange={handleDateRangeChange}
            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
            style={{ borderColor: COLORS.primary, color: COLORS.neutral }}
          >
            <option value="today">Aujourd&apos;hui</option>
            <option value="week">7 derniers jours</option>
            <option value="month">30 derniers jours</option>
            <option value="all">Toutes les données</option>
            <option value="custom">Période personnalisée</option>
          </select>
        </div>
        
        {showDatePicker && (
          <div className="mt-4 p-4 border border-gray-200 rounded-md" ref={datePickerRef}>
            <div className="flex flex-col md:flex-row md:items-center space-y-4 md:space-y-0 md:space-x-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date de début</label>
                <DatePicker
                  selected={startDate}
                  onChange={date => setStartDate(date)}
                  selectsStart
                  startDate={startDate}
                  endDate={endDate}
                  dateFormat="dd/MM/yyyy"
                  className="w-full p-2 border border-gray-300 rounded-md"
                  maxDate={new Date()}
                  locale="fr"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date de fin</label>
                <DatePicker
                  selected={endDate}
                  onChange={date => setEndDate(date)}
                  selectsEnd
                  startDate={startDate}
                  endDate={endDate}
                  minDate={startDate}
                  maxDate={new Date()}
                  dateFormat="dd/MM/yyyy"
                  className="w-full p-2 border border-gray-300 rounded-md"
                  locale="fr"
                />
              </div>
              <div className="flex items-end">
                <button 
                  onClick={() => fetchAnalytics()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  Appliquer
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Contenu conditionnel basé sur l'onglet actif */}
      {activeTab === 'visits' && (
  <div>
    {loading ? (
      <div>
        {stats.loadingProgress ? (
          renderProgressBar(stats.loadingProgress)
        ) : (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2" style={{ borderColor: COLORS.primary }}></div>
          </div>
        )}
      </div>
    ) : (
      <div className="space-y-8">
        <h2 className="text-2xl font-bold text-gray-800 border-b pb-2">Statistiques de visites</h2>
        {/* Première rangée de widgets */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Statistiques générales */}
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <h2 className="text-lg font-semibold mb-4" style={{ color: COLORS.primary }}>Visites totales</h2>
            <p className="text-4xl font-bold" style={{ color: COLORS.primary }}>{stats.totalVisits}</p>
            <p className="text-sm text-gray-500 mt-2">sur la période sélectionnée</p>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm">
            <h2 className="text-lg font-semibold mb-4" style={{ color: COLORS.secondary }}>Visiteurs uniques</h2>
            <p className="text-4xl font-bold" style={{ color: COLORS.secondary }}>{stats.uniqueVisitors}</p>
            <p className="text-sm text-gray-500 mt-2">adresses IP distinctes</p>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm">
            <h2 className="text-lg font-semibold mb-4" style={{ color: COLORS.tertiary }}>Pages par visiteur</h2>
            <p className="text-4xl font-bold" style={{ color: COLORS.tertiary }}>
              {stats.uniqueVisitors ? (stats.totalVisits / stats.uniqueVisitors).toFixed(2) : '0'}
            </p>
            <p className="text-sm text-gray-500 mt-2">moyenne sur la période</p>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm">
            <h2 className="text-lg font-semibold mb-4" style={{ color: COLORS.quaternary }}>Pages vues</h2>
            <p className="text-4xl font-bold" style={{ color: COLORS.quaternary }}>
              {Object.values(stats.pageViews).reduce((a, b) => a + b, 0)}
            </p>
            <p className="text-sm text-gray-500 mt-2">total sur la période</p>
          </div>
        </div>

        {/* Graphique de tendance des visites */}
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <h2 className="text-xl font-semibold mb-4" style={{ color: COLORS.primary }}>Tendance des visites</h2>
          <VisitsTrendChart />
        </div>

        {/* Deuxième rangée */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Pages les plus visitées */}
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <h2 className="text-xl font-semibold mb-4" style={{ color: COLORS.primary }}>Pages les plus visitées</h2>
            <TopPagesChart />
          </div>

          {/* Navigateurs */}
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <h2 className="text-xl font-semibold mb-4" style={{ color: COLORS.secondary }}>Navigateurs</h2>
            <BrowsersPieChart />
          </div>
        </div>

        {/* Troisième rangée */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Appareils */}
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <h2 className="text-xl font-semibold mb-4" style={{ color: COLORS.primary }}>Types d&apos;appareils</h2>
            <DevicesPieChart />
          </div>

          {/* Référents */}
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <h2 className="text-xl font-semibold mb-4" style={{ color: COLORS.secondary }}>Principaux référents</h2>
            {stats.topReferrers.length > 0 ? (
              <div className="space-y-2">
                {stats.topReferrers.map((referrer, index) => (
                  <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-sm truncate" style={{ maxWidth: '70%' }} title={referrer.url}>
                      {referrer.url}
                    </span>
                    <span className="text-sm font-semibold px-3 py-1 rounded-full" style={{ backgroundColor: `${COLORS.chart[index % COLORS.chart.length]}20`, color: COLORS.chart[index % COLORS.chart.length] }}>{referrer.count}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">Aucun référent enregistré</p>
            )}
          </div>
        </div>
      </div>
    )}
  </div>
)}

{/* Statistiques du chatbot */}
      {activeTab === 'chatbot' && (
  <div>
    {chatLoading ? (
      <div>
        {chatStats.loadingProgress ? (
          renderProgressBar(chatStats.loadingProgress)
        ) : (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2" style={{ borderColor: COLORS.secondary }}></div>
          </div>
        )}
      </div>
    ) : (
      <div className="space-y-8">
        <h2 className="text-2xl font-bold text-gray-800 border-b pb-2">Statistiques du Chatbot</h2>
        
        {/* Widgets des statistiques du chatbot */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Total des messages */}
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <h2 className="text-lg font-semibold mb-4" style={{ color: COLORS.secondary }}>Total des messages</h2>
            <p className="text-4xl font-bold" style={{ color: COLORS.secondary }}>{chatStats.totalMessages}</p>
            <p className="text-sm text-gray-500 mt-2">sur la période sélectionnée</p>
          </div>

          {/* Utilisateurs uniques */}
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <h2 className="text-lg font-semibold mb-4" style={{ color: COLORS.primary }}>Utilisateurs uniques</h2>
            <p className="text-4xl font-bold" style={{ color: COLORS.primary }}>{chatStats.uniqueUsers}</p>
            <p className="text-sm text-gray-500 mt-2">adresses IP distinctes</p>
          </div>

          {/* Messages par utilisateur */}
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <h2 className="text-lg font-semibold mb-4" style={{ color: COLORS.tertiary }}>Messages par utilisateur</h2>
            <p className="text-4xl font-bold" style={{ color: COLORS.tertiary }}>
              {chatStats.uniqueUsers ? (chatStats.totalMessages / chatStats.uniqueUsers).toFixed(2) : '0'}
            </p>
            <p className="text-sm text-gray-500 mt-2">moyenne sur la période</p>
          </div>

          {/* Langues utilisées */}
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <h2 className="text-lg font-semibold mb-4" style={{ color: COLORS.quaternary }}>Langues utilisées</h2>
            <p className="text-4xl font-bold" style={{ color: COLORS.quaternary }}>
              {Object.keys(chatStats.messagesByLanguage).length}
            </p>
            <p className="text-sm text-gray-500 mt-2">langues détectées</p>
          </div>
        </div>

        {/* Graphique des messages du chatbot */}
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <h2 className="text-xl font-semibold mb-4" style={{ color: COLORS.secondary }}>Tendance des messages</h2>
          <ChatMessagesChart />
        </div>

        {/* Deuxième rangée - Chatbot */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Intents du chatbot */}
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <h2 className="text-xl font-semibold mb-4" style={{ color: COLORS.primary }}>Intents les plus fréquents</h2>
            <ChatIntentsChart />
          </div>

          {/* Langues utilisées */}
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <h2 className="text-xl font-semibold mb-4" style={{ color: COLORS.secondary }}>Répartition par langue</h2>
            <ChatLanguagesPieChart />
          </div>
        </div>
        
        {/* Sujets de conversation */}
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <h2 className="text-xl font-semibold mb-4" style={{ color: COLORS.primary }}>Sujets de conversation fréquents</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={chatStats.topTopics}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="count"
                    nameKey="name"
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(1)}%)`}
                    labelLine={false}
                  >
                    {chatStats.topTopics.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS.chart[index % COLORS.chart.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value, name) => [`${value} messages (${((value / chatStats.totalMessages) * 100).toFixed(1)}%)`, name]}
                    contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', borderRadius: '8px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div>
              <h3 className="text-lg font-medium mb-3" style={{ color: COLORS.secondary }}>Détails des sujets</h3>
              <div className="space-y-3 max-h-[220px] overflow-y-auto pr-2">
                {chatStats.topTopics.length > 0 ? (
                  chatStats.topTopics.map((topic, index) => (
                    <div key={index} className="flex justify-between items-center p-3 border rounded-md" 
                      style={{ borderColor: `${COLORS.chart[index % COLORS.chart.length]}40`, backgroundColor: `${COLORS.chart[index % COLORS.chart.length]}10` }}>
                      <div>
                        <span className="font-medium" style={{ color: COLORS.chart[index % COLORS.chart.length] }}>{topic.name}</span>
                        <div className="text-xs text-gray-500 mt-1">{((topic.count / chatStats.totalMessages) * 100).toFixed(1)}% des conversations</div>
                      </div>
                      <span className="px-3 py-1 rounded-full text-sm font-semibold" 
                        style={{ backgroundColor: `${COLORS.chart[index % COLORS.chart.length]}20`, color: COLORS.chart[index % COLORS.chart.length] }}>
                        {topic.count}
                      </span>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500">Aucun sujet de conversation enregistré</p>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {/* Messages récents */}
        <div className="bg-white p-6 rounded-lg shadow-sm mt-6">
          <h2 className="text-xl font-semibold mb-4" style={{ color: COLORS.primary }}>Messages récents</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Message</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Intent</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sujet</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Langue</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {chatStats.recentMessages && chatStats.recentMessages.length > 0 ? (
                  chatStats.recentMessages.map((msg, index) => (
                    <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {format(new Date(msg.created_at), 'dd MMMM yyyy HH:mm', { locale: fr })}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">{msg.message}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full" 
                          style={{ backgroundColor: `${COLORS.primary}20`, color: COLORS.primary }}>
                          {msg.intent || 'Non défini'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                          style={{ backgroundColor: `${COLORS.secondary}20`, color: COLORS.secondary }}>
                          {msg.chat_topic || 'Non défini'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{msg.language || 'Inconnu'}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="px-6 py-4 text-center text-sm text-gray-500">Aucun message récent</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    )}
  </div>
)}
    </div>
  );
};

export default Analytics;
